import React, { useState, useEffect, useRef, useCallback } from 'react';

function generateMoodDescription({ mood = '', scenario = '', scene_tags = [], recommended_genres = [], artist = [] }) {
  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  const moodTitle = mood
    .split(' ')
    .map(capitalize)
    .join(' ');

  // Use scenario if available, otherwise use scene_tags
  const scenarioText = scenario 
    ? scenario.split('-').map(capitalize).join(' ')
    : (scene_tags && scene_tags.length > 0
        ? scene_tags.map(t => t.toLowerCase()).join(', ')
        : 'varied emotions');

  const genreList = recommended_genres.length > 0
    ? recommended_genres.map(g => g.toLowerCase()).join(', ')
    : 'diverse genres';

  const artistLine =
    artist && artist.length > 0
      ? `featuring ${artist.join(', ')}, `
      : '';

  return `A blend of ${genreList}, ${artistLine}tuned to the mood of "${moodTitle}" and scenario of ${scenarioText}.`;
}

// Expandable Track Info Component
function ExpandableTrackInfo({ trackName, artistName, trackNameStyle, artistStyle }) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  
  // Smart truncation that doesn't cut words in the middle
  const truncateText = (text, maxLength) => {
    if (text.length <= maxLength) return text;
    
    // Find the last space before the max length to avoid cutting words
    const truncated = text.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');
    
    // If we found a space and it's not too close to the beginning, use it
    if (lastSpace > maxLength * 0.7) {
      return text.substring(0, lastSpace) + '...';
    }
    
    // Otherwise, just cut at max length
    return truncated + '...';
  };
  
  // Check if either track name or artist name is too long (needs truncation)
  const trackNameTooLong = trackName.length > 25;
  const artistNameTooLong = artistName.length > 25;
  const needsExpansion = trackNameTooLong || artistNameTooLong;
  
  // Show truncated text when not expanded, full text when expanded
  const displayTrackName = isExpanded ? trackName : 
    (trackNameTooLong ? truncateText(trackName, 25) : trackName);
  const displayArtistName = isExpanded ? artistName : 
    (artistNameTooLong ? truncateText(artistName, 25) : artistName);
  
  
  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
      {/* Original text with expand button */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        gap: '6px',
        minHeight: '40px',
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        boxSizing: 'border-box'
      }}>
        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <div style={{
            ...trackNameStyle, 
            whiteSpace: isExpanded ? 'normal' : 'nowrap', 
            overflow: 'hidden', 
            textOverflow: isExpanded ? 'unset' : 'ellipsis', 
            wordBreak: isExpanded ? 'break-word' : 'normal'
          }}>
            {displayTrackName}
          </div>
          <div style={{
            ...artistStyle, 
            whiteSpace: isExpanded ? 'normal' : 'nowrap', 
            overflow: 'hidden', 
            textOverflow: isExpanded ? 'unset' : 'ellipsis', 
            wordBreak: isExpanded ? 'break-word' : 'normal'
          }}>
            {displayArtistName}
          </div>
        </div>
        {needsExpansion && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            style={{
              background: 'none',
              border: 'none',
              color: '#6b7280',
              cursor: 'pointer',
              padding: '4px',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '20px',
              height: '20px',
              borderRadius: '3px',
              transition: 'all 0.2s ease',
              flexShrink: 0,
              marginTop: '2px',
              minWidth: '20px',
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f3f4f6';
              e.target.style.color = '#374151';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#6b7280';
            }}
            title={isExpanded ? 'Show less' : 'Show more'}
          >
            {isExpanded ? '−' : '+'}
          </button>
        )}
      </div>
      
    </div>
  );
}

// Companion Message Component with Typing Animation
function CompanionMessage({ text }) {
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(true);

  // Warm, personal signatures with handwritten-style emojis
  const signatures = [
    "— your MoodTune friend ☺",
    "— with love, your music companion ♥",
    "— your understanding friend ✨",
    "— your musical soulmate ❤︎",
    "— your vibe curator ❀",
    "— your emotional DJ ♫",
    "— always here with a smile ☀",
    "— your gentle companion ♡",
    "— in harmony with you ♪",
    "— your steady note of warmth 🤍",
    "— a spark of music ✦",
    "— your guiding melody 🌟",
    "— your little tune of joy ✿",
    "— keeping the rhythm with you ♫",
    "— a friendly chord ☺",
    "— your soft echo of care 🌸",
    "— your simple smile ❤︎",
    "— your heartfelt note ❀",
    "— in tune with your feelings ✨"
  ];
  
  const [signature] = useState(signatures[Math.floor(Math.random() * signatures.length)]);
  
  // Combine text and signature for continuous typing
  const fullMessage = text + '\n\n' + signature;

  useEffect(() => {
    if (!text) return;
    
    setDisplayedText('');
    setIsTyping(true);
    
    let currentIndex = 0;
    const typingSpeed = 35; // slightly slower for more warmth
    
    const typeText = () => {
      if (currentIndex < fullMessage.length) {
        setDisplayedText(fullMessage.slice(0, currentIndex + 1));
        currentIndex++;
        setTimeout(typeText, typingSpeed);
      } else {
        setIsTyping(false);
      }
    };
    
    // Start typing after a short delay
    const startDelay = setTimeout(typeText, 600);
    
    return () => clearTimeout(startDelay);
  }, [text, fullMessage]);

  // Split the displayed text to handle different alignments
  const textParts = displayedText.split('\n\n');
  const mainText = textParts[0] || '';
  const signatureText = textParts[1] || '';

  return (
    <div style={styles.companionBox}>
      <div style={styles.companionContent}>
        <div style={styles.companionText} className="companion-text">
          {mainText}
          {signatureText && (
            <>
              {'\n\n'}
              <span style={styles.signatureText}>
                {signatureText}
              </span>
            </>
          )}
          {isTyping && <span style={styles.cursor}>|</span>}
        </div>
      </div>
    </div>
  );
}

export default function TrackList({ 
  tracks, 
  showCreateButton, 
  onDeleteTrack, 
  deletedTrackIds, 
  showGetMoreSongs, 
  onGetMoreSongs, 
  loadingMoreSongs,
  companionShownEarly 
}) {
  // State for touch interactions on mobile/tablet
  const [touchedTrackId, setTouchedTrackId] = useState(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  // Detect if device supports touch
  useEffect(() => {
    const checkTouchDevice = () => {
      setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
    };
    checkTouchDevice();
    window.addEventListener('resize', checkTouchDevice);
    return () => window.removeEventListener('resize', checkTouchDevice);
  }, []);

  if (!tracks || !tracks.moodDescriptions || !tracks.validTrackInfo) return null;

  const trackList = tracks.validTrackInfo.filter(track => !deletedTrackIds.has(track._id));
  const moodSummary = generateMoodDescription(tracks.moodDescriptions);

  // Touch interaction handlers
  const getTrackKey = (track) => track?._id || track?.spotifyId || `${track?.name || ''}-${track?.artist || ''}`;

  const handleTrackTouch = (track, event) => {
    if (!isTouchDevice) return;
    
    event.stopPropagation();
    const trackKey = getTrackKey(track);
    
    if (touchedTrackId === trackKey) {
      handlePreviewClick(track);
      setTouchedTrackId(null);
    } else {
      // First touch or touch on different track - show hover effect
      setTouchedTrackId(trackKey);
    }
  };

  const handleOutsideTouch = (event) => {
    if (!isTouchDevice) return;
    
    // If touch is not on a track card, clear the touched state
    if (!event.target.closest('.cardContainer')) {
      setTouchedTrackId(null);
    }
  };

  // Add global touch listener to handle outside touches
  useEffect(() => {
    if (isTouchDevice) {
      document.addEventListener('touchstart', handleOutsideTouch);
      return () => document.removeEventListener('touchstart', handleOutsideTouch);
    }
  }, [isTouchDevice]);

  const handlePreviewClick = async (track) => {
    const trackKey = getTrackKey(track);
    if (!trackKey) {
      return;
    }

    setTouchedTrackId(null);

    if (previewState.currentTrackId === trackKey) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setPreviewState(initialPreviewState);
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    const cachedUrl = previewCacheRef.current[trackKey];
    if (cachedUrl) {
      setPreviewState({
        currentTrackId: trackKey,
        trackTitle: track.name,
        artistName: track.artist,
        previewUrl: cachedUrl,
        loading: false,
        error: null
      });
      return;
    }

    setPreviewState({
      currentTrackId: trackKey,
      trackTitle: track.name,
      artistName: track.artist,
      previewUrl: '',
      loading: true,
      error: null
    });

    try {
      const searchTerm = encodeURIComponent(`${track.name} ${track.artist}`.trim());
      const url = `https://itunes.apple.com/search?term=${searchTerm}&entity=song&limit=1`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Preview request failed (${response.status})`);
      }
      const data = await response.json();
      const previewUrl = data?.results?.[0]?.previewUrl || '';

      if (previewUrl) {
        previewCacheRef.current[trackKey] = previewUrl;
        setPreviewState({
          currentTrackId: trackKey,
          trackTitle: track.name,
          artistName: track.artist,
          previewUrl,
          loading: false,
          error: null
        });
      } else {
        setPreviewState({
          currentTrackId: trackKey,
          trackTitle: track.name,
          artistName: track.artist,
          previewUrl: '',
          loading: false,
          error: 'Preview not available'
        });
      }
    } catch (error) {
      console.error('Error fetching preview:', error);
      setPreviewState({
        currentTrackId: trackKey,
        trackTitle: track.name,
        artistName: track.artist,
        previewUrl: '',
        loading: false,
        error: 'Preview unavailable'
      });
    }
  };
  
  // Playlist creation state
  const [playlistUrl, setPlaylistUrl] = useState(null);
  const [creatingPlaylist, setCreatingPlaylist] = useState(false);

  const initialPreviewState = {
    currentTrackId: null,
    trackTitle: '',
    artistName: '',
    previewUrl: '',
    loading: false,
    error: null
  };

  const [previewState, setPreviewState] = useState(initialPreviewState);
  const previewCacheRef = useRef({});
  const audioRef = useRef(null);

  // Reset playlist state when tracks change (new request)
  useEffect(() => {
    if (!playlistUrl) {
      console.log('🔄 Resetting playlist state (new tracks)');
      setPlaylistUrl(null);
    }
  }, [tracks.validTrackInfo, playlistUrl]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPreviewState(initialPreviewState);
  }, [tracks.validTrackInfo]);

  useEffect(() => {
    const audioEl = audioRef.current;
    if (!audioEl) return;

    if (previewState.previewUrl) {
      audioEl.load();
      const playPromise = audioEl.play();
      if (playPromise?.catch) {
        playPromise.catch((err) => {
          console.warn('Audio playback prevented:', err);
        });
      }
    } else {
      audioEl.pause();
      audioEl.currentTime = 0;
    }
  }, [previewState.previewUrl]);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  // Clear stored snapshot once playlist URL is available
  useEffect(() => {
    if (playlistUrl && typeof window !== 'undefined') {
      window.sessionStorage.removeItem('moodtune_search_snapshot');
    }
  }, [playlistUrl]);

  /**
   * Detect if device is mobile
   */
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (window.innerWidth <= 768 && 'ontouchstart' in window);
  };

  /**
   * Create playlist with stored access token
   */
  const createPlaylistWithToken = useCallback(async (sessionToken) => {
    setCreatingPlaylist(true);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      
      console.log('🎵 Creating playlist with session token:', sessionToken);
      
      const response = await fetch(`${backendUrl}/api/create-playlist-with-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionToken
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create playlist');
      }

      const data = await response.json();
      
      console.log('✅ Playlist created successfully:', data.playlistUrl);
      
      setPlaylistUrl(data.playlistUrl);
      setCreatingPlaylist(false);
      
    } catch (error) {
      console.error('❌ Error creating playlist:', error);
      alert(`Failed to create playlist: ${error.message}`);
      setCreatingPlaylist(false);
    }
  }, []); // Empty dependencies since we're using state setters which are stable

  /**
   * Handle Spotify playlist creation
   * Uses popup for desktop, full page redirect for mobile
   */
  const handleCreatePlaylist = async () => {
    // Filter out deleted tracks for playlist creation
    const filteredTracks = tracks.validTrackInfo.filter(track => !deletedTrackIds.has(track._id));
    
    // Persist current track state so we can restore after OAuth redirect (mobile)
    if (typeof window !== 'undefined') {
      try {
        const snapshotTracks = {
          ...tracks,
          validTrackInfo: filteredTracks,
        };

        const snapshot = {
          tracks: snapshotTracks,
          showCreateButton: true,
          deletedTrackIds: [],
          showGetMoreSongs,
          loadingMoreSongs,
          companionShownEarly,
          trackIdCounter: snapshotTracks.validTrackInfo?.length || 0,
        };

        window.sessionStorage.setItem('moodtune_search_snapshot', JSON.stringify(snapshot));
      } catch (persistError) {
        console.warn('Failed to persist search snapshot:', persistError);
      }
    }
    
    // Store the filtered playlist data on the server
    let sessionToken = null;
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/store-playlist-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          playlistMeta: tracks.playlistMeta || {
            name: `MoodTune - ${tracks.moodDescriptions.mood || 'Your Mood'}`,
            description: generateMoodDescription(tracks.moodDescriptions)
          },
          validTrackInfo: filteredTracks
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to store playlist data');
      }

      const data = await response.json();
      sessionToken = data.sessionToken;
      
      console.log('✅ Playlist data stored with session token:', sessionToken);
    } catch (error) {
      if (typeof window !== 'undefined') {
        window.sessionStorage.removeItem('moodtune_search_snapshot');
      }
      console.error('Error storing playlist data:', error);
      alert('Failed to prepare playlist data. Please try again.');
      return;
    }

    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
    const isMobile = isMobileDevice();
    
    if (isMobile) {
      // Mobile: Use full page redirect
      const authUrl = `${backendUrl}/create-playlist?sessionToken=${sessionToken}`;
      window.location.href = authUrl;
    } else {
      // Desktop: Use popup for Spotify auth ONLY
      const authUrl = `${backendUrl}/create-playlist?sessionToken=${sessionToken}`;
      
      const popup = window.open(
        authUrl, 
        'spotify-auth', 
        'width=500,height=600,scrollbars=yes,resizable=yes'
      );
      
      if (!popup) {
        alert('Popup blocked! Please allow popups and try again.');
        return;
      }
      
      // Listen for auth success message from popup
      const handleMessage = (event) => {
        console.log('📨 Received message:', event.data);
        
        // Verify this is the auth success message
        if (event.data?.type === 'spotify-auth-success' && event.data?.sessionToken) {
          console.log('✅ Auth success, creating playlist...');
          
          // Remove listener
          window.removeEventListener('message', handleMessage);
          
          // Popup will close itself
          // Now create the playlist in the main window
          createPlaylistWithToken(event.data.sessionToken);
          
        } else if (event.data?.error) {
          console.error('❌ Auth error:', event.data.error);
          alert(`Authentication failed: ${event.data.error}`);
          window.removeEventListener('message', handleMessage);
        }
      };
      
      window.addEventListener('message', handleMessage);
      
      // Also check if popup was closed manually (with COOP error handling)
      const checkPopupClosed = setInterval(() => {
        try {
          if (popup.closed) {
            clearInterval(checkPopupClosed);
            window.removeEventListener('message', handleMessage);
            
            // If playlist creation didn't start, user probably cancelled
            if (!creatingPlaylist && !playlistUrl) {
              console.log('ℹ️ Popup closed without completing auth');
            }
          }
        } catch (error) {
          // COOP policy may block access to popup.closed
          if (error.name === 'SecurityError' || error.message.includes('Cross-Origin-Opener-Policy')) {
            console.log('COOP policy blocks popup.closed check, relying on postMessage');
            clearInterval(checkPopupClosed);
          }
        }
      }, 1000);
    }
  };

  // Check URL parameters for auth success (mobile redirect)
  // This runs once on mount to check if we're returning from Spotify OAuth redirect
  useEffect(() => {
    console.log('🔍 Checking URL parameters...');
    console.log('📍 Current URL:', window.location.href);
    
    // Get URL search parameters
    const urlParams = new URLSearchParams(window.location.search);
    const authSuccess = urlParams.get('authSuccess');
    const sessionTokenFromUrl = urlParams.get('sessionToken');
    
    // Legacy support: also check for direct playlist URL (old flow)
    const playlistUrlFromParams = urlParams.get('playlistUrl');
    const success = urlParams.get('success');
    
    console.log('📦 URL params:', { 
      authSuccess,
      sessionToken: sessionTokenFromUrl ? 'found' : 'not found',
      playlistUrlFromParams: playlistUrlFromParams ? 'found' : 'not found',
      success
    });
    
    // New flow: Auth success from mobile redirect
    if (authSuccess === 'true' && sessionTokenFromUrl) {
      console.log('✅ Auth success from mobile redirect, creating playlist...');
      
      // Clean up URL parameters
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      
      // Create playlist with the session token
      createPlaylistWithToken(sessionTokenFromUrl);
      
    } 
    // Legacy flow: Direct playlist URL (backward compatibility)
    else if (playlistUrlFromParams && success === 'true') {
      const decodedUrl = decodeURIComponent(playlistUrlFromParams);
      console.log('✅ Found playlist URL in URL parameters (legacy):', decodedUrl);
      
      setPlaylistUrl(decodedUrl);
      
      // Clean up URL parameters
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      
      console.log('✅ Successfully restored playlist URL from URL parameters');
    } else {
      console.log('ℹ️ No auth or playlist data in URL parameters');
    }
  }, [createPlaylistWithToken]); // Include createPlaylistWithToken in dependencies

  return (
    <div style={styles.container}>
      {/* CSS Animations and Font Import for Companion */}
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Caveat:wght@400;500;600;700&display=swap');
          
          .companion-text {
            font-family: "Caveat", cursive !important;
            font-weight: 500 !important;
          }
          
          @keyframes gentleBounce {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-3px); }
          }
          
          @keyframes blink {
            0%, 50% { opacity: 1; }
            51%, 100% { opacity: 0; }
          }
          
          .cardContainer:hover .deleteButton {
            opacity: 1 !important;
          }

          /* Touch device styles */
          @media (hover: none) and (pointer: coarse) {
            .deleteButton {
              opacity: 0 !important;
              transition: opacity 0.2s ease !important;
            }
            
            .cardContainer.touched .deleteButton {
              opacity: 1 !important;
            }
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes fadeIn {
            0% { 
              opacity: 0; 
              transform: translateY(-10px);
            }
            100% { 
              opacity: 1; 
              transform: translateY(0);
            }
          }
          
          .insightCard:hover {
            transform: translateY(-2px) !important;
            background: rgba(255,255,255,0.25) !important;
            box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
          }
          
          /* Responsive styles */
          @media (max-width: 768px) {
            .track-grid {
              grid-template-columns: repeat(2, 1fr) !important;
              gap: 1rem !important;
            }
            .track-card {
              padding: 1rem !important;
              min-height: 160px !important;
            }
            .track-image {
              height: 100px !important;
              object-fit: contain !important;
              width: 100% !important;
              background-color: #f9fafb !important;
            }
            .track-name {
              font-size: 0.9rem !important;
            }
            .track-artist {
              font-size: 0.8rem !important;
            }
            .playlist-card {
              padding: 1.5rem !important;
            }
            .playlist-title {
              font-size: 1.5rem !important;
            }
            .insight-card {
              min-height: 100px !important;
              padding: 0.8rem !important;
            }
            .insight-icon {
              font-size: 1.2rem !important;
            }
            .insight-label {
              font-size: 0.7rem !important;
            }
            .insight-value {
              font-size: 0.85rem !important;
            }
          }
          
          @media (max-width: 480px) {
            .track-grid {
              grid-template-columns: repeat(2, 1fr) !important;
              gap: 0.2rem !important;
              padding: 0 0.2rem !important;
            }
            .track-card {
              min-height: 180px !important;
              border-radius: 6px !important;
              width: 100% !important;
              max-width: calc(50vw - 0.9rem) !important;
            }
            .track-image {
              height: 120px !important;
              object-fit: contain !important;
              border-radius: 4px 4px 0 0 !important;
              background-color: #f9fafb !important;
              width: 100% !important;
            }
            .track-name {
              font-size: 0.7rem !important;
              line-height: 1.1 !important;
              margin-bottom: 0.15rem !important;
              font-weight: 600 !important;
              word-wrap: break-word !important;
            }
            .track-artist {
              font-size: 0.6rem !important;
              line-height: 1.0 !important;
              color: #6b7280 !important;
              word-wrap: break-word !important;
            }
            .playlist-card {
              padding: 1rem !important;
            }
            .playlist-title {
              font-size: 1.3rem !important;
            }
            .insight-card {
              min-height: 80px !important;
              padding: 0.6rem !important;
            }
            .insight-icon {
              font-size: 1rem !important;
            }
            .insight-label {
              font-size: 0.65rem !important;
            }
            .insight-value {
              font-size: 0.8rem !important;
            }
          }
        `}
      </style>
      
      {/* Companion Comment Section - only show if not shown early */}
      {tracks.moodDescriptions?.companion && !companionShownEarly && (
        <CompanionMessage text={tracks.moodDescriptions.companion} />
      )}

      <div style={styles.summaryBox} className="playlist-card">
        <div style={styles.playlistHeader}>
          <div style={styles.playlistInfo}>
            <h3 style={styles.summaryTitle} className="playlist-title">Your Mood-Based Playlist</h3>
            <div style={styles.trackCount}>{trackList.length} tracks curated for you</div>
          </div>
        </div>
        
        <div style={styles.moodInsights}>
          <div style={styles.insightCard} className="insightCard insight-card">
            <div style={styles.insightIcon} className="insight-icon">🎭</div>
            <div style={styles.insightContent}>
              <div style={styles.insightLabel} className="insight-label">Mood</div>
              <div style={styles.insightValue} className="insight-value">
                {tracks.moodDescriptions.mood 
                  ? tracks.moodDescriptions.mood.split(' ').map(word => 
                      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                    ).join(' ')
                  : 'Your Feelings'
                }
              </div>
            </div>
          </div>
          
          <div style={styles.insightCard} className="insightCard insight-card">
            <div style={styles.insightIcon} className="insight-icon">🎵</div>
            <div style={styles.insightContent}>
              <div style={styles.insightLabel} className="insight-label">Genres</div>
              <div style={styles.insightValue} className="insight-value">
                {tracks.moodDescriptions.recommended_genres?.length > 0 
                  ? tracks.moodDescriptions.recommended_genres.slice(0, 3).map(genre =>
                      genre.split(' ').map(word => 
                        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                      ).join(' ')
                    ).join(', ')
                  : 'Diverse Mix'
                }
              </div>
            </div>
          </div>
          
          <div style={styles.insightCard} className="insightCard insight-card">
            <div style={styles.insightIcon} className="insight-icon">✨</div>
            <div style={styles.insightContent}>
              <div style={styles.insightLabel} className="insight-label">Scenario</div>
              <div style={styles.insightValue} className="insight-value">
                {(() => {
                  // Use scenario if available, otherwise use scene_tags
                  const scenario = tracks.moodDescriptions.scenario 
                    ? tracks.moodDescriptions.scenario.split('-').map(word => 
                          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                        ).join(' ')
                    : (tracks.moodDescriptions.scene_tags?.length > 0
                        ? tracks.moodDescriptions.scene_tags.slice(0, 2).map(tag =>
                            tag.split(' ').map(word => 
                              word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                            ).join(' ')
                          ).join(', ')
                        : 'Emotional Journey');
                  
                  const artists = tracks.moodDescriptions.artist?.length > 0 
                    ? tracks.moodDescriptions.artist.slice(0, 2).map(artist =>
                        artist.split(' ').map(word => 
                          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                        ).join(' ')
                      )
                    : [];
                  
                  let result = scenario;
                  if (artists.length > 0) {
                    result += `, featuring ${artists.join(', ')}`;
                  }
                  
                  return result;
                })()}
              </div>
            </div>
          </div>
        </div>
        
        <div style={styles.playlistActions}>
          <div style={styles.actionHint}>
            <span>
              {isTouchDevice 
                ? "Tap the play button on a track to hear a quick preview" 
                : "Click the play button on any track to hear a quick preview"
              }
            </span>
          </div>
        </div>
      </div>

      {/* Playlist Creation Section */}
      {/* Show button if: (1) showCreateButton is true, OR (2) playlistUrl exists (returning from redirect) */}
      {(showCreateButton || playlistUrl || creatingPlaylist) && (
        creatingPlaylist ? (
          // Show loading state while creating playlist
          <div style={{
            textAlign: 'center',
            animation: 'fadeIn 0.3s ease-out',
            padding: '0.75rem',
            borderRadius: '8px',
            backgroundColor: '#f0f9ff',
            marginBottom: '2rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
              <div style={{
                width: '20px',
                height: '20px',
                border: '2px solid #e0e7ff',
                borderTop: '2px solid #6366f1',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}></div>
              <span style={{ color: '#1e40af', fontWeight: 500 }}>Creating your playlist...</span>
            </div>
          </div>
        ) : playlistUrl ? (
          // Show success message with "Open in Spotify" link when playlist is created
          <div style={{
            textAlign: 'center',
            animation: 'fadeIn 0.3s ease-out',
            padding: '0.75rem',
            borderRadius: '8px',
            backgroundColor: '#f0fdf4',
            marginBottom: '2rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#10b981" viewBox="0 0 16 16">
                <path d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zM4.5 7.5a.5.5 0 0 0 0 1h5.793l-2.147 2.146a.5.5 0 0 0 .708.708l3-3a.5.5 0 0 0 0-.708l-3-3a.5.5 0 1 0-.708.708L10.293 7.5H4.5z"/>
              </svg>
              <h4 style={{ margin: 0, color: '#166534', fontWeight: 600 }}>Playlist ready!</h4>
            </div>
            <a 
              href={playlistUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.25rem',
                color: '#1DB954',
                fontWeight: 500,
                textDecoration: 'none',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                e.target.style.textDecoration = 'none';
              }}
            >
              Open in Spotify
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zM4.5 7.5a.5.5 0 0 0 0 1h5.793l-2.147 2.146a.5.5 0 0 0 .708.708l3-3a.5.5 0 0 0 0-.708l-3-3a.5.5 0 1 0-.708.708L10.293 7.5H4.5z"/>
              </svg>
            </a>
          </div>
        ) : (
          // Show "Create Spotify Playlist" button when playlist is not created
          <button
            type="button"
            onClick={handleCreatePlaylist}
            style={{
              ...styles.spotifyBtn,
              backgroundColor: '#1DB954',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s ease',
              marginBottom: '2rem',
              width: '100%'
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm3.669 11.538a.498.498 0 0 1-.686.165c-1.879-1.147-4.243-1.407-7.028-.77a.499.499 0 0 1-.222-.973c3.048-.696 5.662-.397 7.77.892a.5.5 0 0 1 .166.686zm.979-2.178a.624.624 0 0 1-.858.205c-2.15-1.321-5.428-1.704-7.972-.932a.625.625 0 0 1-.362-1.194c2.905-.881 6.517-.454 8.986 1.063a.624.624 0 0 1 .206.858zm.084-2.268C10.154 5.56 5.9 5.419 3.438 6.166a.748.748 0 1 1-.434-1.432c2.825-.857 7.523-.692 10.492 1.07a.747.747 0 0 1-.764 1.288z"/>
            </svg>
            Create Spotify Playlist
          </button>
        )
      )}

      <div style={styles.grid} className="track-grid">
        {tracks.validTrackInfo.map((track) => {
          if (deletedTrackIds.has(track._id)) return null;

          const trackKey = getTrackKey(track);
          const isCurrentTrack = previewState.currentTrackId === trackKey;
          const isPreviewPlaying = isCurrentTrack && Boolean(previewState.previewUrl);
          const isLoadingPreview = previewState.loading && isCurrentTrack;
          const previewErrorForTrack = isCurrentTrack ? previewState.error : null;
          
          return (
            <div
              key={trackKey}
              className={`cardContainer ${isTouchDevice && touchedTrackId === trackKey ? 'touched' : ''}`}
              style={{
                ...styles.cardContainer,
                ...(isTouchDevice && touchedTrackId === trackKey ? {
                  transform: 'scale(1.03)',
                  boxShadow: '0 6px 16px rgba(0, 0, 0, 0.1)'
                } : {})
              }}
              onMouseOver={!isTouchDevice ? (e) => {
                e.currentTarget.style.transform = 'scale(1.03)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.1)';
              } : undefined}
              onMouseOut={!isTouchDevice ? (e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = styles.card.boxShadow;
              } : undefined}
              onTouchStart={(e) => handleTrackTouch(track, e)}
            >
              <button
                className="deleteButton"
                style={styles.deleteButton}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDeleteTrack(track._id);
                }}
                onTouchStart={(e) => {
                  e.stopPropagation();
                }}
                title="Delete track"
              >
                ×
              </button>
              <div
                style={styles.card}
                className="track-card"
              >
                <img
                  src={track.image}
                  alt={`${track.name} cover`}
                  style={styles.image}
                  className="track-image"
                />
                <div style={styles.cardText}>
                  <ExpandableTrackInfo 
                    trackName={track.name}
                    artistName={track.artist}
                    trackNameStyle={styles.trackName}
                    artistStyle={styles.artist}
                  />
                </div>
                <div style={styles.cardActions}>
                  <button
                    type="button"
                    style={{
                      ...styles.previewButton,
                      ...(isPreviewPlaying && !isLoadingPreview ? styles.previewButtonActive : {}),
                      ...(isLoadingPreview ? { opacity: 0.7, cursor: 'wait' } : {})
                    }}
                    disabled={isLoadingPreview}
                    onTouchStart={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handlePreviewClick(track);
                    }}
                  >
                    {isLoadingPreview
                      ? 'Loading Preview...'
                      : isPreviewPlaying
                        ? 'Pause Preview'
                        : 'Play Preview'}
                  </button>
                  {previewErrorForTrack && !isLoadingPreview && (
                    <span style={styles.previewErrorText}>{previewErrorForTrack}</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {(previewState.loading || previewState.previewUrl || previewState.error) && (
        <div style={styles.previewPanel}>
          <div style={styles.previewInfo}>
            <div style={styles.previewTitle}>{previewState.trackTitle || 'Song preview'}</div>
            <div style={styles.previewArtist}>{previewState.artistName}</div>
          </div>
          {previewState.loading ? (
            <div style={styles.previewStatus}>Loading preview...</div>
          ) : previewState.error ? (
            <div style={styles.previewStatusError}>{previewState.error}</div>
          ) : (
            <audio
              key={previewState.previewUrl}
              ref={audioRef}
              controls
              src={previewState.previewUrl}
              style={styles.audioPlayer}
              onEnded={() => setPreviewState(initialPreviewState)}
              onError={() => setPreviewState(prev => ({
                ...prev,
                previewUrl: '',
                loading: false,
                error: 'Preview unavailable'
              }))}
            />
          )}
        </div>
      )}

      {/* Get More Songs Button or Loading Animation - positioned at bottom after all tracks */}
      {showGetMoreSongs && (
        loadingMoreSongs ? (
          <div style={{
            ...styles.getMoreSongsBtn,
            backgroundColor: '#f3f4f6',
            border: 'none',
            color: '#6b7280',
            cursor: 'default',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            transition: 'all 0.2s ease',
            marginTop: '2rem',
            width: '100%'
          }}>
            <div style={styles.loadingSpinner}></div>
            Getting More Songs...
          </div>
        ) : (
          <button
            type="button"
            onClick={onGetMoreSongs}
            style={{
              ...styles.getMoreSongsBtn,
              backgroundColor: '#6366f1',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s ease',
              marginTop: '2rem',
              width: '100%'
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
              <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
            </svg>
            Get More Songs
          </button>
        )
      )}
    </div>
  );
}

const styles = {
  container: {
    marginTop: '2rem',
    fontFamily: '"Segoe UI", sans-serif',
  },
  companionBox: {
    background: '#FDF6F0',
    border: '2px solid #f3e8d3',
    borderRadius: '20px',
    padding: '2rem',
    marginBottom: '2rem',
    position: 'relative',
    boxShadow: '0 12px 40px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.06)',
    transition: 'all 0.3s ease',
    overflow: 'hidden',
    '::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '4px',
      background: 'linear-gradient(90deg, #f59e0b, #d97706, #b45309)',
    }
  },
  companionContent: {
    position: 'relative',
  },
  companionText: {
    fontSize: '1.5rem',
    color: '#451a03',
    lineHeight: 1.5,
    margin: 0,
    fontFamily: '"Caveat", cursive, "Brush Script MT", sans-serif',
    fontWeight: 500,
    letterSpacing: '0.01em',
    textShadow: '0 1px 2px rgba(0,0,0,0.1)',
    whiteSpace: 'pre-line',
    fontDisplay: 'swap',
  },
  signatureText: {
    display: 'block',
    textAlign: 'right',
    marginTop: '0.5rem',
  },
  cursor: {
    color: '#f59e0b',
    fontWeight: 'bold',
    animation: 'blink 1s infinite',
    marginLeft: '2px',
  },
  summaryBox: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '2rem',
    borderRadius: '20px',
    marginBottom: '2rem',
    boxShadow: '0 20px 40px rgba(102, 126, 234, 0.15), 0 8px 16px rgba(0,0,0,0.1)',
    color: 'white',
    position: 'relative',
    overflow: 'hidden',
  },
  playlistHeader: {
    marginBottom: '1.5rem',
  },
  playlistInfo: {
    textAlign: 'center',
  },
  summaryTitle: {
    fontSize: '1.8rem',
    marginBottom: '0.5rem',
    color: 'white',
    fontWeight: '700',
    textShadow: '0 2px 4px rgba(0,0,0,0.3)',
  },
  trackCount: {
    fontSize: '0.95rem',
    color: 'rgba(255,255,255,0.9)',
    fontWeight: '500',
  },
  moodInsights: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  insightCard: {
    background: 'rgba(255,255,255,0.15)',
    backdropFilter: 'blur(10px)',
    borderRadius: '12px',
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    border: '1px solid rgba(255,255,255,0.2)',
    transition: 'all 0.3s ease',
    minHeight: '120px',
  },
  insightIcon: {
    fontSize: '1.5rem',
    marginBottom: '0.5rem',
    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))',
  },
  insightContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
  insightLabel: {
    fontSize: '0.8rem',
    color: 'rgba(255,255,255,0.8)',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '0.5rem',
    textAlign: 'center',
    minHeight: '1.2rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  insightValue: {
    fontSize: '0.95rem',
    color: 'white',
    fontWeight: '600',
    lineHeight: 1.3,
    textAlign: 'center',
  },
  playlistActions: {
    display: 'flex',
    justifyContent: 'center',
  },
  actionHint: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.9rem',
    color: 'rgba(255,255,255,0.8)',
    fontWeight: '400',
    fontStyle: 'italic',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '1rem',
  },
  cardContainer: {
    position: 'relative',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  },
  card: {
    textDecoration: 'none',
    color: 'inherit',
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease, height 0.3s ease',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '280px', // Minimum height, can grow when expanded
  },
  deleteButton: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: '#ef4444',
    color: 'white',
    border: 'none',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transition: 'opacity 0.2s ease',
    zIndex: 10,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
  },
  image: {
    width: '100%',
    height: '200px', // Fixed height to make it square-ish
    objectFit: 'contain', // This will fit the image without cropping, maintaining aspect ratio
    display: 'block',
    backgroundColor: '#f9fafb', // Background color for non-square images
  },
  cardText: {
    padding: '0.75rem 1rem',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minHeight: '80px', // Ensure consistent text area height
  },
  cardActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    padding: '0 1rem 1rem',
    marginTop: 'auto'
  },
  trackName: {
    fontWeight: '600',
    fontSize: '1rem',
    marginBottom: '0.25rem',
    color: '#111827',
  },
  artist: {
    fontSize: '0.875rem',
    color: '#6b7280',
  },
  spotifyBtn: {
    padding: '0.75rem 1.5rem',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '1rem',
    fontWeight: '600',
    transition: 'all 0.3s ease',
    marginTop: '0.5rem',
  },
  getMoreSongsBtn: {
    padding: '0.75rem 1.5rem',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '1rem',
    fontWeight: '600',
    transition: 'all 0.3s ease',
    marginTop: '0.5rem',
  },
  loadingSpinner: {
    width: '16px',
    height: '16px',
    border: '2px solid #e5e7eb',
    borderTop: '2px solid #6366f1',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  previewPanel: {
    marginTop: '2rem',
    padding: '1rem 1.25rem',
    borderRadius: '16px',
    border: '1px solid #e5e7eb',
    backgroundColor: '#f9fafb',
    boxShadow: '0 12px 24px rgba(15, 23, 42, 0.08)',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem'
  },
  previewInfo: {
    display: 'flex',
    flexDirection: 'column'
  },
  previewTitle: {
    fontWeight: 600,
    fontSize: '1rem',
    color: '#111827'
  },
  previewArtist: {
    fontSize: '0.875rem',
    color: '#6b7280'
  },
  previewStatus: {
    fontSize: '0.875rem',
    color: '#6366f1'
  },
  previewStatusError: {
    fontSize: '0.875rem',
    color: '#ef4444'
  },
  audioPlayer: {
    width: '100%'
  },
  previewButton: {
    padding: '0.5rem 0.75rem',
    borderRadius: '8px',
    border: '1px solid #6366f1',
    backgroundColor: '#eef2ff',
    color: '#4338ca',
    fontWeight: 600,
    fontSize: '0.85rem',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    width: '100%',
    textAlign: 'center'
  },
  previewButtonActive: {
    backgroundColor: '#6366f1',
    color: '#ffffff',
    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)'
  },
  previewErrorText: {
    fontSize: '0.75rem',
    color: '#ef4444',
    textAlign: 'center'
  }
};
